import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { PrecioBinanceService } from '../../services/precio-binance.service';
import type {
  EstadoTramosUsdtResponse,
  UsdtVesConfigResponse,
  VesUsdtConfigResponse,
} from '../../services/main-rates.service';
import * as P from './precio-binance-bloque.rules';
import { etiquetaRangoUsdt } from './tramos-usdt-editor.rules';
import { esModeloDistancia } from './tramos-distancia.rules';

type ConfigSentido = UsdtVesConfigResponse | VesUsdtConfigResponse | null | undefined;

/** Alto del gráfico en px (las etiquetas de la derecha se separan con esto). */
const ALTO_GRAFICO = 240;
const SEPARACION_ETIQUETAS = 36;

interface EtiquetaColumna extends P.LineaGrafico {
  topPx: number;
}

/**
 * Bloque «Dónde está cada precio» (pantalla 4 del mockup de tramos), uno por
 * sentido dentro de las tarjetas USDT→VES y VES→USDT del home de operador.
 *
 * Solo lectura y solo referencia: Binance P2P en vivo (compra y venta), el
 * costo (promedio de bancos), nuestra base, techo y piso de los tramos y la
 * tasa del otro sentido, con un gráfico de niveles.
 *
 * Las configs de los dos sentidos llegan del panel (ya se refrescan cada
 * minuto); Binance y los tramos los lee este bloque con el latido compartido
 * de `PrecioBinanceService` (uno solo para los dos bloques) y sólo mientras
 * `activo` (la sección abierta). Si algo falla el bloque se achica (sin
 * Binance, sin tramos) pero el panel sigue.
 */
@Component({
  selector: 'app-precio-binance-bloque',
  templateUrl: './precio-binance-bloque.component.html',
})
export class PrecioBinanceBloqueComponent implements OnInit, OnChanges, OnDestroy {
  @Input() direccion: P.DireccionPrecio = 'VES-USDT';
  /** Config del sentido de este bloque (`rates/{par}/config`). */
  @Input() config: ConfigSentido = null;
  /** Config del otro sentido, para mostrar su tasa. */
  @Input() configContraria: ConfigSentido = null;
  /**
   * La sección que lo contiene está abierta. Plegada no consulta nada; al
   * abrirse lee en el acto y se suma al latido compartido.
   */
  @Input() activo = true;
  /** Respaldo del permiso de la tabla de distancia si el API no lo manda. */
  @Input() puedeEditar = false;

  static readonly REFRESCO_MS = PrecioBinanceService.REFRESCO_MS;
  readonly altoGrafico = ALTO_GRAFICO;

  binance: P.PrecioP2pUsdtVes | null = null;
  /** Ya hubo al menos una respuesta (aunque haya sido «no disponible»). */
  binanceLeido = false;
  tramos: EstadoTramosUsdtResponse | null = null;
  cargando = false;

  costo: number | null = null;
  base: number | null = null;
  contraria: number | null = null;
  techo: number | null = null;
  piso: number | null = null;
  precioBinance: number | null = null;
  filas: P.FilaComparacion[] = [];
  resumen: { texto: string; tono: 'bueno' | 'malo' | 'neutro' } = { texto: '', tono: 'neutro' };
  grafico: P.ModeloGrafico = { lineas: [], puntos: [], banda: null };
  etiquetas: EtiquetaColumna[] = [];

  private latidoSub?: Subscription;
  private sub?: Subscription;
  private iniciado = false;

  constructor(private precios: PrecioBinanceService) {}

  ngOnInit(): void {
    this.iniciado = true;
    this.ajustarSondeo();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (this.iniciado && changes?.['activo']) this.ajustarSondeo();
    this.recalcular();
  }

  ngOnDestroy(): void {
    this.latidoSub?.unsubscribe();
    this.latidoSub = undefined;
    this.sub?.unsubscribe();
  }

  /** Abierto: lee ya y se suma al latido compartido. Plegado: se baja del latido. */
  private ajustarSondeo(): void {
    if (this.activo && !this.latidoSub) {
      this.cargar();
      this.latidoSub = this.precios.latido().subscribe(() => this.cargar());
    } else if (!this.activo && this.latidoSub) {
      this.latidoSub.unsubscribe();
      this.latidoSub = undefined;
    }
  }

  get esVesUsdt(): boolean {
    return this.direccion === 'VES-USDT';
  }

  /**
   * El API ya cobra con el modelo de distancia (Oscar, 23/10/2026): el bloque
   * muestra «Lo que cotiza el motor ahora» en lugar del rango, el gráfico y la
   * tabla A/B/C. Con la API vieja (sin `modelo`) todo queda como antes.
   */
  get esDistancia(): boolean {
    return esModeloDistancia(this.tramos as any);
  }

  /** Comisión configurada del sentido (solo lectura, para la cuenta del PISO/TECHO). */
  get comision(): number | null {
    const c = Number(this.config?.data?.datosEnVivo?.comisionConfigurada);
    return this.config?.data?.datosEnVivo?.comisionConfigurada != null && Number.isFinite(c) ? c : null;
  }

  get hayTramos(): boolean {
    return P.tramosValidos(this.tramos?.tramos).length > 0;
  }

  /** Textos que dependen del sentido (rango de tramos, subtítulo, leyenda). */
  get rotulos(): P.RotulosSentido {
    return P.rotulosSentido(this.direccion);
  }

  /** «1.007,40»: dos decimales en es-CL; «—» si no hay dato. */
  num(x: number | null | undefined): string {
    if (x == null || !Number.isFinite(Number(x))) return '—';
    return Number(x).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** «+1,25 %» / «−0,40 %»: coma y espacio antes del signo %. */
  pctSigno(x: number | null | undefined): string {
    if (x == null || !Number.isFinite(Number(x))) return '—';
    const n = Number(x);
    return `${n > 0 ? '+' : n < 0 ? '−' : ''}${this.num(Math.abs(n))} %`;
  }

  get ladoRelevante(): 'compra' | 'venta' {
    return P.ladoDe(this.direccion);
  }

  get leidoHace(): string | null {
    return P.hace(this.binance?.leidoEn);
  }

  get binanceViejo(): boolean {
    return P.binanceViejo(this.binance?.leidoEn);
  }

  cargar(): void {
    this.cargando = true;
    this.sub?.unsubscribe();
    const tramos$ = this.esVesUsdt
      ? this.precios.obtenerTramosVesUsdt()
      : this.precios.obtenerTramosUsdtVes();
    this.sub = forkJoin({ binance: this.precios.obtenerPrecioUsdtVes(), tramos: tramos$ }).subscribe({
      next: ({ binance, tramos }) => {
        this.binance = binance;
        this.binanceLeido = true;
        // Una lectura periódica que falla (null) no borra la anterior: si no,
        // con el modelo de distancia volvería por un minuto el diseño tachado.
        if (tramos || !this.tramos) this.tramos = tramos;
        this.cargando = false;
        this.recalcular();
      },
      // El servicio nunca lanza; esto es por si acaso.
      error: () => {
        this.binanceLeido = true;
        this.cargando = false;
        this.recalcular();
      },
    });
  }

  private tasaDe(c: ConfigSentido): number | null {
    const d = c?.data;
    const v = Number(d?.tasaVigente?.tasaFinal ?? d?.datosEnVivo?.tasaFinalCalculada);
    return v > 0 ? v : null;
  }

  private rango = (t: P.TramoTasa, _i: number, ultimo: boolean): string =>
    etiquetaRangoUsdt(t.desde, t.hasta, ultimo);

  recalcular(): void {
    const costo = Number(this.config?.data?.datosEnVivo?.promedioBinanceActual);
    this.costo = costo > 0 ? costo : null;
    const baseTramos = Number(this.tramos?.base);
    this.base = baseTramos > 0 ? baseTramos : this.tasaDe(this.config);
    this.contraria = this.tasaDe(this.configContraria);
    const tramos = this.tramos?.tramos;
    const tp = P.techoPiso(this.base, tramos);
    this.techo = tp.techo;
    this.piso = tp.piso;
    this.precioBinance = P.binanceDe(this.direccion, this.binance);
    this.filas = P.filasComparacion(this.direccion, this.base, tramos, this.precioBinance, this.costo, this.rango);
    this.resumen = P.resumenVsBinance(this.filas, this.precioBinance != null);

    this.grafico = P.modeloGrafico({
      techo: this.techo,
      piso: this.piso,
      binance: this.precioBinance,
      costo: this.costo,
      contraria: this.contraria,
      tramos,
      etiquetaRango: this.rango,
      etiquetas: this.esVesUsdt
        ? {
            techo: this.hayTramos ? 'Cobramos lo máximo (tramo chico)' : 'Base Aguacate',
            piso: 'Cobramos lo mínimo (tramo grande)',
            binance: 'Binance en vivo (compra)',
            costo: 'Costo (promedio bancos)',
            contraria: 'USDT → VES (otro sentido)',
          }
        : {
            techo: this.hayTramos ? 'Pagamos lo máximo (tramo grande)' : 'Base Aguacate',
            piso: 'Pagamos lo mínimo (tramo chico)',
            binance: 'Binance en vivo (venta)',
            costo: 'Costo (promedio bancos)',
            contraria: 'VES → USDT (otro sentido)',
          },
    });
    const tops = this.grafico.lineas.map((l) => (l.topPct / 100) * ALTO_GRAFICO - 10);
    const sep = P.separarEtiquetas(tops, ALTO_GRAFICO + 10, SEPARACION_ETIQUETAS);
    this.etiquetas = this.grafico.lineas.map((l, i) => ({ ...l, topPx: sep[i] }));
  }

  /** Colores de la pantalla 4 del mockup. */
  colorLinea(clave: P.ClaveLinea): string {
    switch (clave) {
      case 'techo': return '#ea580c';
      case 'piso': return '#15803d';
      case 'binance': return '#2563eb';
      case 'costo': return '#6b7280';
      default: return '#7c3aed';
    }
  }

  bordeLinea(clave: P.ClaveLinea): string {
    const c = this.colorLinea(clave);
    switch (clave) {
      case 'techo':
      case 'piso':
        return `3px solid ${c}`;
      case 'contraria':
        return `2px dotted ${c}`;
      default:
        return `2px dashed ${c}`;
    }
  }

  colorTexto(clave: P.ClaveLinea): string {
    switch (clave) {
      case 'techo': return '#9a3412';
      case 'piso': return '#14532d';
      case 'binance': return '#1e3a8a';
      case 'costo': return '#374151';
      default: return '#5b21b6';
    }
  }

  colorPunto(p: P.PuntoGrafico, i: number): string {
    const fila = this.filas[i];
    if (fila?.ganaBinance === true) return '#15803d';
    return i === 0 ? '#ea580c' : '#f97316';
  }
}
